import React, {
	Component,
	useState,
	useRef,
	useEffect,
} from "react";

import Flickity from "react-flickity-component";

import "../../../webflow/flickity.css";

import styles from "./ImageSlider.module.css";


/*
 * ============================================================
 * SLIDE
 * ============================================================
 *
 * strip
 *
 * Existing ImageSlider presentation.
 *
 *
 * gallery
 *
 * One full-width image at a time.
 *
 * Individual slides have no rounding.
 *
 * The gallery viewport owns clipping / rounding instead.
 */

class ImgSlide extends Component {
	render() {
		const {
			index,
			mode = "strip",
			...props
		} = this.props;


		if (
			mode ===
			"gallery"
		) {
			return (
				<div
					{...props}
					className={
						styles.gallerySlide
					}
				>
					<div
						className={
							styles.galleryImage
						}
						style={{
							backgroundImage:
								`url("${props.src}")`,
						}}
					/>
				</div>
			);
		}


		/*
		 * ========================================================
		 * LEGACY / STRIP MODE
		 * ========================================================
		 *
		 * Same dimensions as the existing component:
		 *
		 * width: 30%
		 * min-width: 300px
		 * max-width: 500px
		 * horizontal padding: 10px
		 * vertical padding: 5px
		 * aspect-ratio: 4 / 5
		 */

		return (
			<div
				{...props}
				className={
					styles.stripSlide
				}
			>
				<div
					className={
						styles.stripImage
					}
					style={{
						backgroundImage:
							`url("${props.src}")`,
					}}
				/>
			</div>
		);
	}
}


/*
 * ============================================================
 * IMAGE SLIDER
 * ============================================================
 *
 * MODES
 *
 *
 * strip
 *
 * Default.
 *
 * This is the existing ImageSlider behaviour.
 *
 *
 * gallery
 *
 * Full-width snap slider with:
 *
 * - Flickity touch / drag
 * - one image at a time
 * - previous / next arrows
 * - thumbnail navigation
 * - active thumbnail state
 * - viewport-only rounding
 */

const ImageSlider =
	React.forwardRef(
		(
			{
				data = [],
				mode = "strip",
				credits,
			},
			ref,
		) => {
			const mainSlider =
				useRef(
					null,
				);

			const [
				flickity,
				setFlickity,
			] = useState(
				null,
			);

			const [
				activeSlide,
				setActiveSlide,
			] = useState(
				0,
			);

			const isPaused =
				useRef(
					false,
				);


			const isGallery =
				mode ===
				"gallery";


			/*
			 * ====================================================
			 * EXISTING STRIP TICKER
			 * ====================================================
			 *
			 * Preserved from the existing component.
			 *
			 * The requestAnimationFrame invocation remains disabled,
			 * exactly as before.
			 */

			const tickerSpeed =
				typeof window !==
				"undefined"
					? window.innerWidth /
					  1000
					: 0;


			const update =
				() => {
					if (
						isPaused.current
					) {
						return;
					}

					if (
						mainSlider.current &&
						mainSlider.current
							.slides
					) {
						mainSlider.current.x =
							(
								mainSlider
									.current
									.x -
								tickerSpeed
							) %
							mainSlider
								.current
								.slideableWidth;

						mainSlider.current.selectedIndex =
							mainSlider.current.dragEndRestingSelect();

						mainSlider.current.updateSelectedSlide();

						mainSlider.current.settle(
							mainSlider
								.current
								.x,
						);
					}

					window.requestAnimationFrame(
						update,
					);
				};


			const pause =
				() => {
					isPaused.current =
						true;
				};


			const play =
				() => {
					if (
						isPaused.current
					) {
						isPaused.current =
							false;
					}
				};


			/*
			 * ====================================================
			 * FLICKITY OPTIONS
			 * ====================================================
			 *
			 * strip:
			 *
			 * Exact existing options.
			 *
			 *
			 * gallery:
			 *
			 * Uses Flickity's normal slide snapping instead of
			 * free scrolling.
			 *
			 * Flickity already provides native mouse/touch dragging.
			 */

			const stripOptions = {
				autoPlay:
					false,

				prevNextButtons:
					false,

				pageDots:
					false,

				draggable:
					true,

				freeScroll:
					true,

				contain:
					true,

				selectedAttraction:
					0.015,

				friction:
					0.25,

				initialIndex:
					0,
			};


			const galleryOptions = {
				autoPlay:
					false,

				prevNextButtons:
					false,

				pageDots:
					false,

				draggable:
					true,

				freeScroll:
					false,

				contain:
					true,

				cellAlign:
					"left",

				selectedAttraction:
					0.025,

				friction:
					0.28,

				initialIndex:
					0,
			};


			const flickityOptions =
				isGallery
					? galleryOptions
					: stripOptions;


			/*
			 * ====================================================
			 * FLICKITY INSTANCE
			 * ====================================================
			 */

			const handleFlickityRef =
				(instance) => {
					mainSlider.current =
						instance;

					setFlickity(
						instance,
					);
				};


			/*
			 * ====================================================
			 * ACTIVE SLIDE
			 * ====================================================
			 *
			 * Used by gallery thumbnails and arrow state.
			 *
			 * Does not affect legacy strip mode.
			 */

			useEffect(() => {
				if (
					!flickity ||
					!isGallery
				) {
					return;
				}


				const handleChange =
					(index) => {
						setActiveSlide(
							index,
						);
					};


				/*
				 * Sync immediately in case Flickity already selected
				 * an item before this effect ran.
				 */

				setActiveSlide(
					flickity.selectedIndex ??
						0,
				);


				flickity.on(
					"change",
					handleChange,
				);


				return () => {
					flickity.off(
						"change",
						handleChange,
					);
				};
			}, [
				flickity,
				isGallery,
			]);


			/*
			 * ====================================================
			 * GALLERY CONTROLS
			 * ====================================================
			 */

			const handlePrevious =
				() => {
					mainSlider.current?.previous?.();
				};


			const handleNext =
				() => {
					mainSlider.current?.next?.();
				};


			const handleSelect =
				(index) => {
					mainSlider.current?.select?.(
						index,
					);
				};


			const canGoPrevious =
				activeSlide >
				0;


			const canGoNext =
				activeSlide <
				data.length -
					1;


			return (
				<div
					className={`${styles.root}${
						isGallery
							? ` ${styles.gallery}`
							: ` ${styles.strip}`
					}`}
					onMouseEnter={
						pause
					}
					onTouchMove={
						pause
					}
					onMouseMove={
						pause
					}
					onMouseLeave={
						play
					}
					onTouchEnd={
						play
					}
					onClick={
						pause
					}
					ref={
						ref
					}
				>
					{/*
					 * =============================================
					 * MAIN SLIDER
					 * =============================================
					 */}

					<div
						className={
							isGallery
								? styles.galleryViewport
								: undefined
						}
					>
						<Flickity
							key={
								mode
							}
							options={
								flickityOptions
							}
							flickityRef={
								handleFlickityRef
							}
						>
							{data.map(
								(
									entry,
									i,
								) => (
									<ImgSlide
										key={
											entry
										}
										index={
											i
										}
										mode={
											mode
										}
										src={
											entry
										}
									/>
								),
							)}
						</Flickity>


						{/*
						 * =========================================
						 * GALLERY ARROWS
						 * =========================================
						 *
						 * Overlayed inside the viewport.
						 */}

						{isGallery &&
						data.length >
							1 ? (
							<>
								<button
									type="button"
									className={`${styles.galleryArrow} ${styles.galleryArrowPrevious}`}
									aria-label="Previous image"
									disabled={
										!canGoPrevious
									}
									onClick={(
										event,
									) => {
										event.preventDefault();

										event.stopPropagation();

										handlePrevious();
									}}
								>
									<span
										className={`${styles.galleryArrowIcon} ${styles.galleryArrowIconPrevious}`}
										aria-hidden="true"
									/>
								</button>


								<button
									type="button"
									className={`${styles.galleryArrow} ${styles.galleryArrowNext}`}
									aria-label="Next image"
									disabled={
										!canGoNext
									}
									onClick={(
										event,
									) => {
										event.preventDefault();

										event.stopPropagation();

										handleNext();
									}}
								>
									<span
										className={`${styles.galleryArrowIcon} ${styles.galleryArrowIconNext}`}
										aria-hidden="true"
									/>
								</button>
							</>
						) : null}
					</div>


					{/*
					 * =============================================
					 * GALLERY THUMBNAILS
					 * =============================================
					 */}

					{isGallery &&
					data.length >
						1 ? (
						<div
							className={
								styles.galleryThumbnails
							}
						>
							{data.map(
								(
									entry,
									i,
								) => {
									const isActive =
										activeSlide ===
										i;

									return (
										<button
											key={
												entry
											}
											type="button"
											className={
												styles.galleryThumbnail
											}
											data-active={
												isActive
													? "true"
													: "false"
											}
											aria-label={`Show image ${i + 1}`}
											aria-current={
												isActive
													? "true"
													: undefined
											}
											onClick={(
												event,
											) => {
												event.preventDefault();

												event.stopPropagation();

												handleSelect(
													i,
												);
											}}
										>
											<span
												className={
													styles.galleryThumbnailImage
												}
												style={{
													backgroundImage:
														`url("${entry}")`,
												}}
											/>
										</button>
									);
								},
							)}
						</div>
					) : null}


					{/*
					 * =============================================
					 * CREDITS
					 * =============================================
					 *
					 * Existing behaviour stays available.
					 */}

					{credits ? (
						<div
							className={
								styles.credits
							}
						>
							<h6>
								{
									credits
								}
							</h6>
						</div>
					) : null}
				</div>
			);
		},
	);


export default ImageSlider;